// Слои режима «Датасет»: картинка снимка из COG, точки АИС дня и кольца записей.
//
// Картинка и АИС лежат ПОД рубежами (`addUnderBoundaries`), кольца — поверх:
// кольцо — ответ, ради которого режим открыт, и рубеж его не перечёркивает.
//
// Радиус кольца — погрешность позиции в МЕТРАХ, а не в пикселях: позиция АИС на
// момент съёмки интерполирована, и судно законно стоит где-то внутри кольца.
// Кольцо постоянного размера утверждало бы точность, которой нет.

import type { DatasetAttempt } from '../api'
import { IMAGERY_MAX_ZOOM } from '../map/rasterZoom'
import { sceneKey, type SceneLike } from './datasetCalendar'
import type { DatasetSetKey } from './imageryUrl'

export const DS_PICTURE_SOURCE = 'imagery-ds-picture'
export const DS_PICTURE_LAYER = 'imagery-ds-picture-raster'
export const DS_FOOTPRINT_SOURCE = 'imagery-ds-footprint'
export const DS_FOOTPRINT_LAYER = 'imagery-ds-footprint-line'
export const DS_AIS_SOURCE = 'imagery-ds-ais'
export const DS_AIS_LAYER = 'imagery-ds-ais-points'
export const DS_RECORDS_SOURCE = 'imagery-ds-records'
export const DS_RECORDS_LAYER = 'imagery-ds-records-ring'
/** Тёмная подводка под кольцом: светлый статус иначе тонет в светлой подложке. */
export const DS_RECORDS_HALO_LAYER = 'imagery-ds-records-halo'
export const DS_MARKS_SOURCE = 'imagery-ds-marks'
export const DS_MARKS_LAYER = 'imagery-ds-marks-dot'

/** Отметка без АИС — сплошная малиновая точка: кольцо записи полое и
 *  размером с погрешность позиции, их не спутать. */
export const MARK_COLOR = '#e879f9'

/** Мельче сервер отдаёт прозрачный тайл (`imagery_cog_tiles.MIN_ZOOM`). */
export const COG_MIN_ZOOM = 6

/** Канал картинки: `nir` — общий с окнами kgd, `visual` — цветной TCI оптики. */
export type CogChannel = 'nir' | 'visual'

export function cogTileUrl(scene: { source: string; scene_id: string },
                           channel: CogChannel = 'nir'): string {
  const url = `/api/v1/tiles/raster/imagery-cog/${scene.source}/${encodeURIComponent(scene.scene_id)}/{z}/{x}/{y}.png`
  return channel === 'visual' ? `${url}?ch=visual` : url
}

/** Пределы объявлены оба: мельче z6 просить нечего, крупнее потолка — 400. */
export function cogSourceSpec(scene: { source: string; scene_id: string },
                              channel: CogChannel = 'nir') {
  return {
    type: 'raster' as const,
    tiles: [cogTileUrl(scene, channel)],
    tileSize: 256,
    minzoom: COG_MIN_ZOOM,
    maxzoom: IMAGERY_MAX_ZOOM,
  }
}

export interface CogPlan {
  action: 'keep' | 'rebuild' | 'remove'
  url: string | null
}

/** Трогать ли слой картинки. Решает АДРЕС тайлов: свежий ответ приносит новый объект снимка с теми же значениями. */
export function cogLayerPlan(currentUrl: string | null,
                             scene: { source: string; scene_id: string } | null,
                             channel: CogChannel = 'nir'): CogPlan {
  if (scene == null) {
    return currentUrl == null ? { action: 'keep', url: null } : { action: 'remove', url: null }
  }
  const url = cogTileUrl(scene, channel)
  return url === currentUrl ? { action: 'keep', url } : { action: 'rebuild', url }
}

/** Цвет статуса записи. Незнакомый — как `new`: пропавшее кольцо не отличить
 *  от записи, которой нет. */
export function statusColor(status: string): string {
  switch (status) {
    case 'identified': return '#22d3ee'
    case 'found': return '#4ade80'
    case 'not_found': return '#f97316'
    case 'excluded': return '#64748b'
    default: return '#e2e8f0'
  }
}

export const RECORD_STATUSES = ['identified', 'found', 'not_found', 'excluded', 'new'] as const

export function statusColorExpression(): unknown[] {
  const pairs = RECORD_STATUSES.filter(s => s !== 'new').flatMap(s => [s, statusColor(s)])
  return ['match', ['get', 'status'], ...pairs, statusColor('new')]
}

/** Окружность Земли, м. MapLibre меряет мир в 512-пиксельных тайлах. */
const EARTH_M = 40075016.686

/** Пикселей на z0 у радиуса `meters` на широте `lat`. */
export function pxAtZoom0(meters: number, lat: number): number {
  return meters * 512 / (EARTH_M * Math.cos((lat * Math.PI) / 180))
}

/** Минимум кольца, px: ниже кольцо не видно и не кликается. */
export const RING_MIN_PX = 5

/** Радиус по зуму: `px0 · 2^z`, не меньше `RING_MIN_PX`. Ступени по целым
 *  зумам — `zoom` в MapLibre допустим только входом верхнего `interpolate`. */
export function ringRadiusExpression(): unknown[] {
  const stops: unknown[] = []
  for (let z = 0; z <= 22; z++) {
    stops.push(z, ['max', RING_MIN_PX, ['*', ['get', 'px0'], 2 ** z]])
  }
  return ['interpolate', ['exponential', 2], ['zoom'], ...stops]
}

export interface DatasetRecord {
  record_id: number
  source: string
  scene_id: string
  mmsi: string
  lat: number
  lon: number
  status: string
  status_reason?: string | null
  pos_uncertainty_m: number
  sog?: number | null
  cog?: number | null
  length_m?: number | null
  cloud_pct_local?: number | null
}

/** Кольца записей выбранного снимка; `null` — всех снимков дня. */
/** Кольца записей снимка. В наборе «пропуски» — только «не найдено»; в
 *  наборе «без АИС» кольца остаются: отметку без пары читают рядом с теми, у
 *  кого пара есть. */
export function recordsCollection(records: DatasetRecord[], scene: string | null,
                                  set: DatasetSetKey = 'all') {
  return {
    type: 'FeatureCollection' as const,
    features: records
      .filter(r => scene == null || sceneKey(r) === scene)
      .filter(r => set !== 'missed' || r.status === 'not_found')
      .map(r => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [r.lon, r.lat] },
        properties: {
          record_id: r.record_id, status: r.status,
          px0: pxAtZoom0(r.pos_uncertainty_m, r.lat),
        },
      })),
  }
}

export interface DatasetMark {
  mark_id: number
  source: string
  scene_id: string
  lat: number
  lon: number
  verdict: string
  length_m: number | null
  nearest_record_m: number | null
  in_tolerance: boolean
  algo_version: string
  evidence: Record<string, unknown>
}

/** Отметки без АИС снимка; в наборе «пропуски» их нет — там спрашивают о другом. */
export function marksCollection(marks: DatasetMark[], scene: string | null, set: DatasetSetKey) {
  return {
    type: 'FeatureCollection' as const,
    features: set === 'missed' ? [] : marks
      .filter(m => scene == null || sceneKey(m) === scene)
      .map(m => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [m.lon, m.lat] },
        properties: { mark_id: m.mark_id },
      })),
  }
}

export interface AisFix {
  mmsi: string
  utc: string
  lat: number
  lon: number
  sog?: number | null
  cog?: number | null
  provider?: string
}

export function aisCollection(fixes: AisFix[]) {
  return {
    type: 'FeatureCollection' as const,
    features: fixes.map(p => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] },
      properties: { mmsi: p.mmsi, utc: p.utc },
    })),
  }
}

export interface DatasetScene extends SceneLike {
  captured_at: string
  cloud_pct_scene: number | null
  footprint: { type: string; coordinates: unknown } | null
  records: Record<string, number>
  /** Отметок без АИС; поле приходит, когда сервер их отдаёт. */
  marks?: number
}

export function footprintCollection(scene: DatasetScene | null) {
  return {
    type: 'FeatureCollection' as const,
    features: scene?.footprint
      ? [{ type: 'Feature' as const, geometry: scene.footprint, properties: {} }]
      : [],
  }
}

/** Рамка контура для `fitBounds`. Контур через шов 180° (долготы обоих знаков
 *  у краёв) переводится в восточную шкалу — иначе рамка охватила бы весь мир. */
export function footprintBounds(coords: [number, number][]): [[number, number], [number, number]] | null {
  if (!coords.length) return null
  const seam = coords.some(c => c[0] > 90) && coords.some(c => c[0] < -90)
  const lons = coords.map(c => (seam && c[0] < 0 ? c[0] + 360 : c[0]))
  const lats = coords.map(c => c[1])
  return [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]]
}

export const DS_INSTRUMENT_SOURCE = 'imagery-ds-instrument'
export const DS_INSTRUMENT_LINE_LAYER = 'imagery-ds-instrument-line'
export const DS_INSTRUMENT_DOT_LAYER = 'imagery-ds-instrument-dot'

export interface InstrumentPoint { lat: number; lon: number; verdict: string | null }

/** Где прибор поставил судно записи: отметка пары ПОСЛЕДНЕЙ попытки. Кольцо
 *  записи стоит на позиции АИС, а не на корпусе; без этой точки сдвиг между
 *  ними (погрешность АИС) читается как ошибка прибора. Последняя попытка без
 *  пары — точки нет: старая пара текущему прибору не принадлежит. */
export function instrumentPoint(attempts: DatasetAttempt[]): InstrumentPoint | null {
  if (attempts.length === 0) return null
  const last = attempts.reduce((a, b) => (b.attempt_id > a.attempt_id ? b : a))
  const mark = last.evidence?.mark as { lat?: unknown; lon?: unknown } | undefined
  if (!mark || typeof mark.lat !== 'number' || typeof mark.lon !== 'number') return null
  return { lat: mark.lat, lon: mark.lon, verdict: last.verdict }
}

/** Пунктир «позиция АИС → точка прибора» и сама точка. */
export function instrumentCollection(ais: { lat: number; lon: number }, p: InstrumentPoint | null) {
  if (!p) return { type: 'FeatureCollection' as const, features: [] }
  return {
    type: 'FeatureCollection' as const,
    features: [
      { type: 'Feature' as const, properties: {},
        geometry: { type: 'LineString' as const, coordinates: [[ais.lon, ais.lat], [p.lon, p.lat]] } },
      { type: 'Feature' as const, properties: { verdict: p.verdict },
        geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] } },
    ],
  }
}
